import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosPrivate } from "@/api/axios";
import { toast } from "sonner";
import { ProductType } from "@/lib/interfaces";
import useProductCategories from "@/hooks/useProductCategories";
import MediaFormField from "@/components/ui/mediaFormField";
import RichTextEditor from "@/components/ui/RichTextEditor";

const formSchema = z.object({
  name: z.string().min(1, "Product Name is required"),
  description: z.string().min(1, "Description is required"),
  category_id: z.coerce.number().min(1, "Category is required"),
  sku: z.string().optional(),
  company: z.string().optional(),
  product_group: z.string().optional(),
  tags: z.string().optional(),
  best_seller: z.string().optional(),
  quantity: z.coerce.number().optional(),
  length: z.coerce.number().optional(),
  width: z.coerce.number().optional(),
  height: z.coerce.number().optional(),
  net_weight: z.coerce.number().optional(),
  gross_weight: z.coerce.number().optional(),
  volume: z.coerce.number().optional(),
});

interface ProductEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductType | null;
  // categories prop optional now; if omitted we fetch internally
  categories?: { id: number; name: string }[] | undefined;
}

export default function ProductEditDialog({ open, onOpenChange, product, categories: categoriesProp }: ProductEditDialogProps) {
  const queryClient = useQueryClient();
  // If caller did not pass categories, fetch them (same behavior as AddProduct)
  const { data: fetchedCategories, isLoading: isLoadingCategories, isError: isCategoriesError, refetch: refetchCategories } = useProductCategories();
  const categories = categoriesProp ?? fetchedCategories;
  // Image states (same pattern as AddProduct)
  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState("");
  const [thumbnail, setThumbnail] = React.useState<File | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = React.useState("");

  // Helper function to normalize category names for comparison
  const normalize = (str: string) => str.toLowerCase().trim();

  // Map API category names to Firebase folder names
  const categoryFolderMap: Record<string, string> = {
    "food and beverages": "F&B",
    "health and dietary suppliments": "H&D", 
    "personal care and cosmetics": "P&C",
    "others": "Others",
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: 0,
      sku: "",
      company: "",
      product_group: "",
      tags: "",
      best_seller: "",
      quantity: undefined,
      length: undefined,
      width: undefined,
      height: undefined,
      net_weight: undefined,
      gross_weight: undefined,
      volume: undefined,
    },
  });

  // Get selected category name for folder mapping
  const selectedCategoryId = form.watch("category_id");
  const selectedCategory = categories?.find((cat) => cat.id === selectedCategoryId);
  const selectedCategoryName = selectedCategory?.name || "";

  // Determine Firebase folder based on selected category
  const getFolderForCategory = (categoryName: string): string => {
    const normalizedName = normalize(categoryName);
  return categoryFolderMap[normalizedName] || "Others";
  };

  // Build dynamic Firebase paths
  const imageRefPath = selectedCategoryName
    ? `DXN Products/${getFolderForCategory(selectedCategoryName)}/images`
    : "product_images";
  const thumbnailRefPath = selectedCategoryName
    ? `DXN Products/${getFolderForCategory(selectedCategoryName)}/thumbnails`
    : "product_thumbnails";

  // Disable uploads until category is selected
  const uploadDisabled = !selectedCategoryName;

  React.useEffect(() => {
    if (product) {
      form.reset({
        name: product.name || "",
        description: product.description || "",
        category_id: product.category_id || 0,
        sku: product.sku || "",
        company: product.company || "",
        product_group: product.product_group || "",
        tags: product.tags || "",
        best_seller: product.best_seller || "",
        quantity: product.quantity || undefined,
        length: product.length || undefined,
        width: product.width || undefined,
        height: product.height || undefined,
        net_weight: product.net_weight || undefined,
        gross_weight: product.gross_weight || undefined,
        volume: product.volume || undefined,
      });
  // Initialize image urls to current product images
  setImage(null);
  setThumbnail(null);
  setImageUrl(product.image_url || "");
  setThumbnailUrl((product as any)?.thumbnail_url || "");
    }
  }, [product, form]);

  const updateProductMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      // Clean payload: remove empty strings and unset optional numeric fields
      const cleaned: Record<string, any> = {};
      Object.entries(data).forEach(([key, val]) => {
        if (val === "") return; // drop empty strings
        if (val === undefined || val === null) return; // drop empties
        if (typeof val === "number" && (Number.isNaN(val) || val === 0)) return; // drop NaN/zero for optional numbers
        cleaned[key] = val;
      });
  // Include uploaded image urls if provided
  if (imageUrl) cleaned.image_url = imageUrl;
  if (thumbnailUrl) cleaned.thumbnail_url = thumbnailUrl;
  const res = await axiosPrivate.put(`/products/${product?.id}`, cleaned);
      if (!res.data.success) throw new Error("Failed to update product");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Product updated successfully");
      // Mark stale and immediately refetch the active list to reflect changes
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.refetchQueries({ queryKey: ["products"], type: "active" });
      onOpenChange(false);
    },
    onError: (error: any) => {
      // Surface backend validation messages if available
      const msg = error?.response?.data?.message || error?.message || "Failed to update product";
      const details = error?.response?.data?.errors;
      toast.error(typeof msg === "string" ? msg : "Failed to update product");
    },
  });

  function handleSubmit(data: z.infer<typeof formSchema>) {
    updateProductMutation.mutate(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
  <DialogContent className="w-full max-w-full !max-w-[98vw] h-[95vh] flex flex-col p-8">
        <DialogHeader>
          <DialogTitle>Edit Product</DialogTitle>
          <DialogDescription>Update product details below and click Save Changes.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6 flex-1 overflow-y-auto" onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Description</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Enter product description"
                        minHeight="250px"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ? String(field.value) : ""}
                        onValueChange={(val) => field.onChange(Number(val))}
                        disabled={isLoadingCategories}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isLoadingCategories ? "Loading categories..." : "Select Category"} />
                        </SelectTrigger>
                        <SelectContent>
                          {isLoadingCategories && (
                            <div className="px-2 py-1 text-sm text-muted-foreground">Loading...</div>
                          )}
                          {!isLoadingCategories && isCategoriesError && (
                            <div className="px-2 py-1 text-sm text-red-500 flex items-center justify-between gap-2">
                              <span>Error loading</span>
                              <button type="button" onClick={() => refetchCategories()} className="underline text-xs">Retry</button>
                            </div>
                          )}
                          {!isLoadingCategories && categories && categories.length === 0 && (
                            <div className="px-2 py-1 text-sm text-muted-foreground flex items-center justify-between gap-2">
                              <span>No categories</span>
                              <button type="button" onClick={() => refetchCategories()} className="underline text-xs">Retry</button>
                            </div>
                          )}
                          {!isLoadingCategories && categories && categories.length > 0 && (
                            categories.map((cat) => (
                              <SelectItem key={cat.id} value={String(cat.id)}>
                                {cat.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SKU</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="product_group"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product Group</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="best_seller"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Best Seller</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Length</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="width"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Width</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="height"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Height</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="net_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Net Weight</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gross_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gross Weight</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="volume"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Volume</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Images (moved to bottom) */}
            <div className="space-y-4">
              <h4 className="text-base font-medium">Images</h4>
              {uploadDisabled && (
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  Please select a category first to enable image uploads
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FormItem>
                    <FormLabel>Product Image</FormLabel>
                    <MediaFormField
                      media={image}
                      setMedia={setImage}
                      setMediaUrl={setImageUrl}
                      refPath={imageRefPath}
                      uploadDisabled={uploadDisabled}
                    />
                  </FormItem>
                </div>
                <div>
                  <FormItem>
                    <FormLabel>Product Thumbnail</FormLabel>
                    <MediaFormField
                      media={thumbnail}
                      setMedia={setThumbnail}
                      setMediaUrl={setThumbnailUrl}
                      refPath={thumbnailRefPath}
                      uploadDisabled={uploadDisabled}
                    />
                  </FormItem>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={updateProductMutation.isPending}>
                {updateProductMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
